import React, { memo, useMemo } from 'react';

export enum HeadingTypes {
  h1,
  h2,
  h3,
  h4,
}

interface HeadingProps {
  text: string;
  headingType: HeadingTypes;
  headingStyleType?: HeadingTypes | null;
  className?: string;
}

const getHeadingSpecificClassNames = (
  headingStyleType: HeadingTypes
): string => {
  switch (headingStyleType) {
    case HeadingTypes.h1:
      return 'text-4xl md:text-6xl';
    case HeadingTypes.h2:
      return 'text-3xl md:text-5xl';
    case HeadingTypes.h3:
      return 'text-2xl md:text-4xl';
    case HeadingTypes.h4:
      return 'text-xl md:text-3xl';
  }
};

const getHeadingClassNames = (
  headingStyleType: HeadingTypes,
  className?: string
): string => {
  const sharedClassNames: string =
    'text-text/body leading-tight mt-0 transition-colors ease-in-out duration-300 group-hover:text-text/body-accent';
  const headingSpecificClassNames: string =
    getHeadingSpecificClassNames(headingStyleType);
  const injectedCustomClassNames: string = className || '';
  return (
    sharedClassNames +
    ' ' +
    headingSpecificClassNames +
    ' ' +
    injectedCustomClassNames
  );
};

const HeadingProvider = (props: {
  headingType: HeadingTypes;
  headingStyleType: HeadingTypes | null;
  className?: string;
  children: string;
}): JSX.Element => {
  /*--
    We set type based on the existence of a headingStyleType, with a fallback of headingType.
    This is used in circumstances whereby a heading needs to look a specific way, but also needs
    to satisfy heading sequential ordering in the dom. E.g - The article card, sequentially, needs
    to be a h3, but in the designs, the styling given to it is equal to a h4
  --*/
  const type: HeadingTypes = props.headingStyleType || props.headingType;
  const headingClassNames: string = getHeadingClassNames(type, props.className);
  switch (props.headingType) {
    case HeadingTypes.h1:
      return <h1 className={headingClassNames}>{props.children}</h1>;
    case HeadingTypes.h2:
      return <h2 className={headingClassNames}>{props.children}</h2>;
    case HeadingTypes.h3:
      return <h3 className={headingClassNames}>{props.children}</h3>;
    case HeadingTypes.h4:
      return <h4 className={headingClassNames}>{props.children}</h4>;
  }
};

const Heading: React.FC<HeadingProps> = ({
  text,
  headingType,
  headingStyleType = null,
  className,
}) => {
  return useMemo(
    () => (
      <HeadingProvider
        headingType={headingType}
        headingStyleType={headingStyleType}
        className={className}
      >
        {text}
      </HeadingProvider>
    ),
    [text, headingType, headingStyleType, className]
  );
};

export default memo(Heading);
